import { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * 149-ar: Dialog paksa ganti password. Ditampilkan saat backend flag
 * `must_change_password=true` (setelah admin reset, atau saat login pertama untuk
 * user baru). Tidak dapat ditutup tanpa selesai — closable=false via onOpenChange.
 */
export default function ForceChangePasswordDialog({ open, tempPassword = "" }) {
  const { refresh, logout } = useAuth();
  const [oldPw, setOldPw] = useState(tempPassword);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error("Konfirmasi password tidak match"); return; }
    if (newPw.length < 8) { toast.error("Password minimal 8 karakter"); return; }
    if (!/[a-zA-Z]/.test(newPw) || !/\d/.test(newPw)) { toast.error("Password harus mengandung huruf & angka"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password berhasil diperbarui");
      await refresh();
      // Trigger reload agar dialog dismiss otomatis (must_change_password=false)
      window.location.reload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal ganti password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* not closable */ }}>
      <DialogContent
        className="rounded-none max-w-md"
        data-testid="dialog-force-change-password"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <KeyRound className="text-[#FF5A1F]" size={20} /> Wajib Ganti Password
          </DialogTitle>
          <DialogDescription>
            Untuk melanjutkan, Anda wajib mengganti password. Password harus <b>minimal 8 karakter</b>, mengandung <b>huruf & angka</b>.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="overline text-slate-600 text-[10px]">Password Sekarang / Sementara</Label>
            <Input type="password" required value={oldPw} onChange={(e) => setOldPw(e.target.value)} data-testid="force-old-pw" className="rounded-none border-slate-300" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-slate-600 text-[10px]">Password Baru</Label>
            <Input type="password" required value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="force-new-pw" className="rounded-none border-slate-300" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-slate-600 text-[10px]">Konfirmasi Password Baru</Label>
            <Input type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="force-confirm-pw" className="rounded-none border-slate-300" />
          </div>
          <div className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 p-2 leading-relaxed">
            Kebijakan: min 8 karakter · huruf & angka · beda dari password lama.
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button type="button" variant="outline" className="rounded-none" onClick={logout} data-testid="force-logout-cancel">Batal & Logout</Button>
            <Button type="submit" disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold" data-testid="force-submit-pw">
              {busy ? "Menyimpan…" : "Simpan Password Baru"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
